import React, {useEffect} from 'react';
import debounce from 'lodash.debounce';
import classNames from 'classnames';
import merge from 'lodash.merge';
import Highcharts from 'highcharts';
import IntersectionObserver from 'react-intersection-observer';
import {Text, Currency} from 'components/UIKit';
import * as styles from './CardChart.scss';
import {CHART_OPTIONS, HIGHCHARTS_OPTIONS} from './CardChartOptions';

const TITLE_HEIGHT = 58;

export interface CardChartProps {
  id: string;
  data: any;
  total: number;
  title: string;
  loading?: boolean;
  layout: string;
  chartOffsets?: number;
  className?: string;
}

export interface CardChartState {
  chartsReady: boolean;
  containerHeight: number | null;
}

export function CardChart({
  id,
  data: chartData,
  total,
  title,
  layout,
  chartOffsets = 0,
  className,
}: CardChartProps) {
  const container: React.RefObject<HTMLDivElement> = React.createRef();
  let chart: Highcharts.Chart | null = null;
  let chartNode!: HTMLElement;

  const buildChart = () => {
    const data = Object.keys(chartData).map((date) => [new Date(date).getTime(), chartData[date]]);

    const chartOptions = merge(
      {
        chart: {
          renderTo: chartNode,
          height: (container.current as HTMLElement).clientHeight - TITLE_HEIGHT - chartOffsets,
        },
        series: [
          {
            id,
            type: 'area',
            data,
          },
        ],
      },
      CHART_OPTIONS,
      HIGHCHARTS_OPTIONS,
    );

    return new Highcharts.Chart(chartOptions);
  };

  const handleResize = debounce(() => {
    if (chart && container.current) {
      const height = (container.current as HTMLElement).clientHeight - TITLE_HEIGHT - chartOffsets;
      chart.setSize(null, height);
    }
  }, 50);

  const handleVisibilityChange = (inView) => {
    if (inView && !chart) {
      chart = buildChart();
      setTimeout(() => (chart as Highcharts.Chart).reflow(), 1);
    }
  };

  const setupChart = (node: HTMLElement | null): void => {
    if (!node) {
      return;
    }

    chartNode = node;
  };

  useEffect(
    () => {
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    },
    [handleResize, id],
  );

  return (
    <IntersectionObserver threshold={1} triggerOnce onChange={handleVisibilityChange}>
      {({ref: intersectionRef}) => (
        <div ref={container} className={classNames(className, styles.container, styles[layout])}>
          <div ref={intersectionRef} className={styles.title}>
            <Currency value={total} display="bold" />
            <Text type="smaller" text={title} component="p" resetMargin />
          </div>
          <div ref={setupChart} />
        </div>
      )}
    </IntersectionObserver>
  );
}
